import { useQuery } from '@tanstack/react-query';
import { BookOpen, FileDown, LayoutGrid, List, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { DocumentSummaryDto, Paginated } from '@imsenta/shared';
import { PageHeader } from '@/components/AppShell';
import { Badge, Button, Card, EmptyState, ErrorState, Input, Select, Spinner, useToast } from '@/components/ui';
import { api, ApiError, downloadFile } from '@/lib/api';
import { usePortal } from '@/lib/portal';
import { fmtDate } from '@/lib/format';

interface CatalogResponse extends Paginated<DocumentSummaryDto> {
  facets: {
    types: { key: string; name: string; count: number }[];
    tags: { tag: string; count: number }[];
  };
}

/**
 * The reading room.
 *
 * Everyone in the organisation can find the policy that applies to them, read
 * it, and take a PDF — with none of the authoring or governance machinery.
 */
export function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [view, setView] = useState<'cards' | 'table'>('cards');
  const { push } = useToast();
  const { isPortal, organisation } = usePortal();
  // On a portal the catalog is the whole application, so it lives at the root.
  const readerPath = (reference: string) => (isPortal ? `/${reference}` : `/catalog/${reference}`);

  const typeKey = params.get('typeKey') ?? '';
  const tag = params.get('tag') ?? '';

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (search) next.set('q', search);
      else next.delete('q');
      if (next.toString() !== params.toString()) setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['catalog', { q: params.get('q'), typeKey, tag }],
    queryFn: () =>
      api.get<CatalogResponse>('/catalog', {
        q: params.get('q') ?? undefined,
        typeKey: typeKey || undefined,
        tag: tag || undefined,
      }),
  });

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const download = async (doc: DocumentSummaryDto) => {
    try {
      await downloadFile(`/documents/${doc.id}/export`, { format: 'pdf' });
      push(`${doc.reference} downloaded`, 'success');
    } catch (error) {
      push(error instanceof ApiError ? error.message : 'Download failed', 'danger');
    }
  };

  return (
    <>
      <PageHeader
        title={isPortal ? (organisation ?? 'Policy catalog') : 'Policy catalog'}
        description="The published policies, standards and procedures that apply to you."
        actions={
          <div className="flex rounded-md border border-ink-200 bg-white p-0.5">
            <button
              type="button"
              onClick={() => setView('cards')}
              className={`rounded p-1.5 ${view === 'cards' ? 'bg-ink-100 text-ink-800' : 'text-ink-400 hover:text-ink-700'}`}
              aria-label="Card view"
            >
              <LayoutGrid className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => setView('table')}
              className={`rounded p-1.5 ${view === 'table' ? 'bg-ink-100 text-ink-800' : 'text-ink-400 hover:text-ink-700'}`}
              aria-label="List view"
            >
              <List className="size-4" />
            </button>
          </div>
        }
      />

      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-56 flex-1">
            <Search className="absolute top-2.5 left-3 size-4 text-ink-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the catalog…" className="pl-9" />
          </div>
          <Select value={typeKey} onChange={(e) => update('typeKey', e.target.value)} className="w-auto min-w-40">
            <option value="">All types</option>
            {data?.facets.types
              .filter((t) => t.count > 0 || t.key === typeKey)
              .map((type) => (
                <option key={type.key} value={type.key}>
                  {type.name}
                </option>
              ))}
          </Select>
        </div>

        {data?.facets.tags.length ? (
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-2.5">
            <span className="mr-1 text-[11.5px] text-ink-400">Tags</span>
            <button
              type="button"
              onClick={() => update('tag', '')}
              className={`rounded-full px-2 py-0.5 text-[11.5px] ${!tag ? 'bg-brand-100 font-medium text-brand-700' : 'bg-ink-100 text-ink-600 hover:bg-ink-200'}`}
            >
              All
            </button>
            {data.facets.tags.map((item) => (
              <button
                key={item.tag}
                type="button"
                onClick={() => update('tag', item.tag === tag ? '' : item.tag)}
                className={`rounded-full px-2 py-0.5 text-[11.5px] ${
                  tag === item.tag ? 'bg-brand-100 font-medium text-brand-700' : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
                }`}
              >
                {item.tag} <span className="text-ink-400">{item.count}</span>
              </button>
            ))}
          </div>
        ) : null}
      </Card>

      {isLoading ? (
        <Spinner label="Loading the catalog…" />
      ) : error ? (
        <Card>
          <ErrorState error={error} onRetry={() => void refetch()} title="Could not load the catalog" />
        </Card>
      ) : !data?.items.length ? (
        <Card>
          <EmptyState
            icon={<BookOpen className="size-10" />}
            title={params.get('q') ? 'Nothing matches that search' : 'No published documents yet'}
            description={
              params.get('q')
                ? 'Try a different term, or clear the filters.'
                : 'Documents appear here once they are published. Drafts stay in the Documents area.'
            }
          />
        </Card>
      ) : view === 'cards' ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.items.map((doc) => {
            const tags = Array.isArray(doc.metadata.tags) ? (doc.metadata.tags as string[]) : [];
            return (
              <Card key={doc.id} className="flex flex-col p-4 transition-shadow hover:shadow-md">
                <div className="mb-2 flex items-center gap-2">
                  <Badge tone="neutral">{doc.reference}</Badge>
                  <Badge tone="brand">{doc.documentTypeName}</Badge>
                  {doc.currentVersion ? <span className="ml-auto text-[11.5px] text-ink-400">{doc.currentVersion.label}</span> : null}
                </div>
                <Link to={readerPath(doc.reference)} className="text-[15px] leading-snug font-semibold text-ink-900 hover:text-brand-700">
                  {doc.title}
                </Link>
                {doc.summary ? <p className="mt-1.5 line-clamp-2 text-[13px] text-ink-500">{doc.summary}</p> : null}
                <div className="mt-3 flex flex-wrap gap-1">
                  {tags.map((t) => (
                    <span key={t} className="rounded bg-ink-100 px-1.5 py-0.5 text-[11px] text-ink-500">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="mt-auto flex items-center justify-between gap-2 pt-4">
                  <span className="text-[11.5px] text-ink-400">
                    {doc.currentVersion?.publishedAt ? `Issued ${fmtDate(doc.currentVersion.publishedAt)}` : ''}
                  </span>
                  <div className="flex gap-1.5">
                    <Button size="sm" icon={<FileDown className="size-3.5" />} onClick={() => download(doc)}>
                      PDF
                    </Button>
                    <Link to={readerPath(doc.reference)}>
                      <Button size="sm" variant="primary">
                        Read
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13.5px]">
              <thead className="border-b border-ink-200 bg-ink-50/60 text-[11.5px] tracking-wide text-ink-500 uppercase">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Reference</th>
                  <th className="px-4 py-2.5 font-semibold">Title</th>
                  <th className="px-4 py-2.5 font-semibold">Type</th>
                  <th className="px-4 py-2.5 font-semibold">Version</th>
                  <th className="px-4 py-2.5 font-semibold">Issued</th>
                  <th className="px-4 py-2.5 font-semibold">Owner</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.items.map((doc) => (
                  <tr key={doc.id} className="hover:bg-ink-50">
                    <td className="px-4 py-2.5 font-mono text-[12px] text-ink-500">{doc.reference}</td>
                    <td className="px-4 py-2.5">
                      <Link to={readerPath(doc.reference)} className="font-medium text-ink-800 hover:text-brand-700">
                        {doc.title}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-ink-600">{doc.documentTypeName}</td>
                    <td className="px-4 py-2.5 text-ink-600">{doc.currentVersion?.label}</td>
                    <td className="px-4 py-2.5 text-ink-600">{fmtDate(doc.currentVersion?.publishedAt)}</td>
                    <td className="px-4 py-2.5 text-ink-600">{doc.owner?.name ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="sm" icon={<FileDown className="size-3.5" />} onClick={() => download(doc)}>
                        PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}
